/* OntologyGraph — signature visuelle de Noam.
 * Graphe SVG animé sobre : halos qui respirent, arêtes qui se dessinent,
 * mise en évidence contextuelle au survol. Aucune dépendance externe.
 */
import { useState } from "react";

const NODES = [
  { id: "evenement", label: "Événement", x: 420, y: 60, c: "#EF9F27" },
  { id: "personne", label: "Personne", x: 130, y: 175, c: "#9AA7B8" },
  { id: "organisation", label: "Organisation", x: 420, y: 235, c: "#EFD127", major: true },
  { id: "actif", label: "Actif", x: 700, y: 160, c: "#378ADD" },
  { id: "fournisseur", label: "Fournisseur", x: 85, y: 290, c: "#D9822B" },
  { id: "deal", label: "Deal", x: 185, y: 400, c: "#8E7CF8" },
  { id: "transaction", label: "Transaction", x: 430, y: 415, c: "#43C6D8" },
  { id: "instrument", label: "Instrument", x: 690, y: 350, c: "#378ADD" },
  { id: "risque", label: "Risque", x: 300, y: 525, c: "#E24B4A" },
  { id: "controle", label: "Contrôle", x: 118, y: 555, c: "#1D9E75" },
  { id: "decision", label: "Décision", x: 545, y: 530, c: "#1D9E75" },
  { id: "outcome", label: "Outcome", x: 730, y: 500, c: "#A78BFA" },
];

const EDGES = [
  { a: "personne", b: "organisation", label: "emploie" },
  { a: "personne", b: "deal", label: "participe" },
  { a: "evenement", b: "organisation", label: "concerne" },
  { a: "organisation", b: "actif", label: "possède" },
  { a: "organisation", b: "fournisseur", label: "s'appuie sur" },
  { a: "fournisseur", b: "risque", label: "expose" },
  { a: "risque", b: "controle", label: "maîtrisé par" },
  { a: "organisation", b: "deal", label: "donne lieu à" },
  { a: "deal", b: "transaction", label: "exécuté via" },
  { a: "transaction", b: "instrument", label: "génère via" },
  { a: "actif", b: "instrument", label: "sous-jacent" },
  { a: "transaction", b: "risque", label: "expose" },
  { a: "risque", b: "decision", label: "influence" },
  { a: "transaction", b: "decision", label: "trace" },
  { a: "decision", b: "outcome", label: "produit" },
];

const byId = Object.fromEntries(NODES.map((n) => [n.id, n]));

export default function OntologyGraph() {
  const [active, setActive] = useState(null);
  const isLinked = (id) =>
    active && (id === active || EDGES.some((e) => (e.a === active && e.b === id) || (e.b === active && e.a === id)));
  const edgeActive = (e) => active && (e.a === active || e.b === active);

  return (
    <div className="relative" data-testid="ontology-graph">
      <style>{`
        @keyframes ontoBreath { 0%,100% { opacity: .25; } 50% { opacity: .6; } }
        @keyframes ontoDraw { from { stroke-dashoffset: 600; } to { stroke-dashoffset: 0; } }
        .onto-halo { animation: ontoBreath 5s ease-in-out infinite; }
        .onto-edge { stroke-dasharray: 600; animation: ontoDraw 2.4s ease-out forwards; }
      `}</style>
      <svg viewBox="0 0 840 630" className="w-full h-auto select-none">
        {/* arêtes */}
        {EDGES.map((e, i) => {
          const A = byId[e.a];
          const B = byId[e.b];
          const mx = (A.x + B.x) / 2;
          const my = (A.y + B.y) / 2;
          const on = edgeActive(e);
          const dim = active && !on;
          return (
            <g key={`${e.a}-${e.b}`} style={{ opacity: dim ? 0.12 : 1, transition: "opacity .4s" }}>
              <line
                x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                stroke={on ? "#EF9F27" : "rgba(255,255,255,0.14)"}
                strokeWidth={on ? 1.4 : 1}
                className="onto-edge"
                style={{ animationDelay: `${0.15 * i}s`, transition: "stroke .4s" }}
              />
              <text x={mx} y={my - 6} textAnchor="middle" fontSize="10" fontStyle="italic"
                fill={on ? "#EF9F27" : "#5B6572"} style={{ transition: "fill .4s" }}>
                {e.label}
              </text>
            </g>
          );
        })}
        {/* nœuds */}
        {NODES.map((n) => {
          const linked = isLinked(n.id);
          const dim = active && !linked;
          const r = n.major ? 26 : 19;
          return (
            <g key={n.id} style={{ opacity: dim ? 0.2 : 1, transition: "opacity .4s", cursor: "pointer" }}
              onMouseEnter={() => setActive(n.id)} onMouseLeave={() => setActive(null)}
              data-testid={`onto-node-${n.id}`}>
              <circle cx={n.x} cy={n.y} r={r + 14} fill={n.c} className="onto-halo"
                style={{ filter: "blur(14px)", opacity: 0.3 }} />
              <circle cx={n.x} cy={n.y} r={r} fill="#0A0E13" stroke={n.c}
                strokeWidth={n.id === active ? 2 : 1.2} style={{ transition: "stroke-width .3s" }} />
              <circle cx={n.x} cy={n.y} r={3} fill={n.c} />
              <text x={n.x} y={n.y + r + 18} textAnchor="middle" fontSize={n.major ? 13 : 11.5}
                fontWeight={n.major ? 700 : 500} fill={linked || !active ? "#E4E7EB" : "#71717a"}
                style={{ letterSpacing: "0.04em", transition: "fill .4s" }}>
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
